import { handleError } from '@pn/core/errors/handleError';
import { isEmbedded } from '@pn/core/utils/embedded';
import { hasKeyWithType } from '@pn/core/utils/logic';
import { identity, isNil, isNumber, isObject, isString } from 'lodash-es';
import { TokenRequestError } from './tokenRequestError';

type Formatter = (token: string) => string;
type GetAccessToken = () => Promise<string>;

export class TokenManager {
  private getAccessToken?: GetAccessToken;
  private cachedToken = '';
  private cachedTokenExpiresAt: number | undefined;
  private hasReportedFailure = false;
  private hasReportedExpiredCache = false;
  private formatter: Formatter;

  constructor(f: Formatter = identity) {
    this.formatter = f;
  }

  set(getAccessToken: GetAccessToken): void {
    this.getAccessToken = getAccessToken;
  }

  /**
   * Retrieves the cached token. Useful in synchronous contexts.
   * Returns `undefined` if no token has been fetched yet.
   */
  getCached(raw = false): string | undefined {
    if (this.cachedToken === '') return undefined;

    this.reportExpiredCache();

    return raw ? this.cachedToken : this.formatter(this.cachedToken);
  }

  /**
   * Retrieves the access token by calling the stored function.
   * @param raw If true, returns the raw token without formatting.
   */
  async fetchToken(raw = false): Promise<string | undefined> {
    if (isEmbedded()) return undefined;
    if (isNil(this.getAccessToken)) return undefined;

    try {
      const token = await this.getAccessToken();

      this.cachedToken = token;
      this.cachedTokenExpiresAt = getExpiresAt(token);
      this.hasReportedFailure = false;
      this.hasReportedExpiredCache = false;

      return raw ? token : this.formatter(token);
    } catch (error) {
      this.reportFailure(error);
      return undefined;
    }
  }

  /**
   * Signed-out users throw on every request, so only failures of a signed-in
   * session are reported. Reported once per run of failures so a retrying
   * caller cannot flood the logger.
   */
  private reportFailure(error: unknown): void {
    if (this.hasReportedFailure) return;

    const wasSignedIn =
      error instanceof TokenRequestError
        ? error.wasSignedIn
        : this.cachedToken !== '';
    if (!wasSignedIn) return;

    this.hasReportedFailure = true;

    if (error instanceof TokenRequestError) {
      handleError({
        error,
        category: `token_refresh_failed:${error.code}`,
        params: {
          ...error.details,
          terminal: error.isTerminal,
          hadTokenThisRun: this.cachedToken !== '',
        },
      });
    } else {
      const code =
        isObject(error) && hasKeyWithType(error, 'error', isString)
          ? error.error
          : 'unknown';

      handleError({ error, category: `token_refresh_failed:${code}` });
    }
  }

  /**
   * Synchronous callers (e.g. map tiles) only see the token as fresh as the
   * last `fetchToken` call. Reported once per token to find out whether they
   * end up sending expired tokens in practice.
   */
  private reportExpiredCache(): void {
    if (this.hasReportedExpiredCache || isNil(this.cachedTokenExpiresAt)) {
      return;
    }

    const expiredForSeconds = Math.round(
      Date.now() / 1000 - this.cachedTokenExpiresAt
    );
    if (expiredForSeconds <= 0) return;

    this.hasReportedExpiredCache = true;

    handleError({
      error: new Error('Cached access token is expired'),
      category: 'cached_token_expired',
      params: { expiredForSeconds },
    });
  }
}

/**
 * Reads the `exp` claim (seconds since epoch) of a JWT access token.
 */
function getExpiresAt(token: string): number | undefined {
  try {
    const payload = token.split('.')[1] ?? '';
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const claims: unknown = JSON.parse(json);

    return isObject(claims) && hasKeyWithType(claims, 'exp', isNumber)
      ? claims.exp
      : undefined;
  } catch {
    return undefined;
  }
}

export const pnTokenManager = new TokenManager((token) => `Bearer ${token}`);
